import type * as colibrioCoreBase from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import type * as colibrioCoreLocator from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import type { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { ContentPositionTimelineUnit } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type {
    IContentLocation, 
    IContentPositionTimeline, 
    IReaderDocument, 
    IReaderPublication, 
    IUnresolvedContentLocation
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { 
    EpubContentPositionTimelineOptions 
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import type { 
    CirrusTimelineReadyEventCallback, 
    ICirrusReaderReadingProgressionTimelineData, 
    ICirrusReaderReadingProgressionTimeline, 
    ICirrusReaderReadingProgressionTimelinePositionData, 
    ICirrusReaderContentPositionData 
} from "../shared/CirrusReaderModel";
import { useCirrusReaderUtils } from '~~/layers/reader/app/utils/CirrusReaderUtils';

export const useCirrusProgressionTimeline = (): ICirrusReaderReadingProgressionTimeline => {
    const { $cirrusReaderPublication, $cirrusReaderView } = useNuxtApp();
    const cirrusReaderUtils = useCirrusReaderUtils();

    let _colibrioTimelineInstance!: IContentPositionTimeline;
    let _colibrioReaderPublication: IReaderPublication;
    let _timelineReadyCallback: CirrusTimelineReadyEventCallback | undefined = undefined;
    let _timelineData: ICirrusReaderReadingProgressionTimelineData | null = null;

    const _create = async (
        initialState: string | undefined = undefined,
        buildProgressCallback: (_progress: number) => void,
    ) => {
        _colibrioReaderPublication = $cirrusReaderPublication.colibrioReaderPublication;
        $cirrusReaderView.setCirrusReaderViewOptions({
            colibrioReaderViewOptions: {
                pageProgressionTimelineOptions: {
                    enabled: false,
                },
            },
        });

        _colibrioTimelineInstance = await _buildTimeline(_colibrioReaderPublication, initialState, buildProgressCallback);
        const documentContentLocations = await _fetchDocumentPositionData(_colibrioTimelineInstance);

        _timelineData = {
            length: _colibrioTimelineInstance.getLength(),
            unit: _colibrioTimelineInstance.getUnit(),
            sections: documentContentLocations,
        };
        if (_timelineReadyCallback) {
            _timelineReadyCallback(_timelineData);
        }
    }



    const _isReady = (): boolean => {
        return _colibrioTimelineInstance !== null && _colibrioTimelineInstance !== undefined;
    }

    const _fetchContentLocation = async (timelinePosition: number): Promise<IContentLocation | undefined> => {
        if (!_colibrioTimelineInstance) {
            return undefined;
        }
        return _colibrioTimelineInstance?.fetchContentLocation(timelinePosition);
    }
    const _fetchTimelinePosition = async (location: colibrioCoreLocator.ILocator): Promise<number | undefined> => {
        if (!_colibrioTimelineInstance) {
            return undefined;
        }
        return _colibrioTimelineInstance?.fetchTimelinePosition(location);
    }
    const _fetchTimelineRange = (location: colibrioCoreLocator.ILocator | colibrioCoreLocator.ILocatorData | IContentLocation | IUnresolvedContentLocation): Promise<colibrioCoreBase.IIntegerRange | undefined> => {
        return _colibrioTimelineInstance?.fetchTimelineRange(location);
    }

    const _fetchContentLocationAsRange = async (start: number, end: number): Promise<IContentLocation | undefined> => {
        return _colibrioTimelineInstance.fetchContentLocationAsRange(start, end);
    }

    const _getLength = (): number => {
        return _colibrioTimelineInstance.getLength() || 0;
    }

    const _updateTimelinePosition = (locator: ILocator): void => {
        const updateFn = async () => {
            if (_colibrioTimelineInstance && locator) {
                const position = await _fetchTimelinePosition(locator);
                if (position !== undefined) useCirrusReaderNavigation().setSliderProgressionValue(position);
            }
        };

        if (_colibrioTimelineInstance) {
            updateFn().catch(console.error);
        }
    }

    const _getBackingTimelineImplementation = (): IContentPositionTimeline => {
        return _colibrioTimelineInstance;
    }

    const _toSerializedData = (): string | undefined => {
        return _colibrioTimelineInstance.toSerializedData();
    }

    const _serialize = async (): Promise<ICirrusReaderReadingProgressionTimelineData | undefined> => {
        if (!_colibrioTimelineInstance) {
            console.warn('CirrusReaderProgressionTimeline.serialize(), No content position timeline instance available. Have you ');
            return;
        }

        const documentContentLocations = await _fetchDocumentPositionData(_colibrioTimelineInstance);

        return {
            length: _colibrioTimelineInstance.getLength(),
            unit: _colibrioTimelineInstance.getUnit(),
            sections: documentContentLocations,
        };
    }

    const _onTimelineReady = (callback: CirrusTimelineReadyEventCallback): void => {
        _timelineReadyCallback = callback;
        if (_colibrioTimelineInstance && _timelineData) {
            callback(_timelineData);
        }
    }



    const _fetchDocumentPositionData = async (timeline: IContentPositionTimeline): Promise<ICirrusReaderReadingProgressionTimelinePositionData[]> => {
        const documents: IReaderDocument[] = _colibrioReaderPublication.getSpine();
        const documentContentLocations: ICirrusReaderReadingProgressionTimelinePositionData[] = [];

        for (const doc of documents) {
            const contentLocation = await doc.fetchContentLocationForEntireDocument();
            const contentPositionData: ICirrusReaderContentPositionData | undefined = await $cirrusReaderView.fetchContentPositionData(contentLocation);

            if (contentPositionData && contentPositionData.locator) {
                const startPosition = await timeline.fetchTimelinePosition(contentLocation.collapseToStart());
                const endPosition = await timeline.fetchTimelinePosition(contentLocation.collapseToEnd());
                documentContentLocations.push({
                    contentPositionData,
                    range: {start: startPosition, end: endPosition},
                });
            }
        }

        return documentContentLocations;
    }

    const _buildTimeline = async (
        colibrioReaderPublication: IReaderPublication,
        state: string | undefined,
        callback: (_progress: number) => void,
    ): Promise<IContentPositionTimeline> => {

        let timelinePromise: Promise<IContentPositionTimeline>;
        
        if (cirrusReaderUtils.isEpubReaderPublication(colibrioReaderPublication)) {
            let timelineOptions: EpubContentPositionTimelineOptions;
            if (colibrioReaderPublication.getAvailableContentPositionTimelineUnits().includes(ContentPositionTimelineUnit.PAGES)) {
                timelineOptions = {
                    unit: ContentPositionTimelineUnit.PAGES,
                };
            } else {
                timelineOptions = {
                    unit: ContentPositionTimelineUnit.WORDS,
                };
            }
            timelineOptions.serializedData = state;

            timelinePromise = colibrioReaderPublication.createContentPositionTimeline(colibrioReaderPublication.getSpine(), timelineOptions, callback);

        } else if (cirrusReaderUtils.isPdfReaderPublication(colibrioReaderPublication)) {

            const timelineOptions: EpubContentPositionTimelineOptions = {
                unit: ContentPositionTimelineUnit.PAGES,
                serializedData: state,
            };
            timelinePromise = colibrioReaderPublication.createContentPositionTimeline(colibrioReaderPublication.getSpine(), timelineOptions, callback);

        } else {
            timelinePromise = Promise.reject('Not an EPUB or PDF');
        }
        return timelinePromise;
    }


    
    return {
        create: _create,
        fetchTimelinePosition: _fetchTimelinePosition,
        fetchContentLocation: _fetchContentLocation,
        fetchContentLocationAsRange: _fetchContentLocationAsRange,
        fetchTimelineRange: _fetchTimelineRange,
        getLength: _getLength,
        isReady: _isReady,
        getBackingTimelineImplementation: _getBackingTimelineImplementation,
        toSerializedData: _toSerializedData,
        updateTimelinePosition: _updateTimelinePosition,
        onTimelineReady: _onTimelineReady,
        serialize: _serialize
    }
}